import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Tooltip } from 'primereact/tooltip';
import { tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj } from '../common/TableSearch';
import { km, lit, pot, mesecNaziv } from './format';
import IzdavanjeDialog from './IzdavanjeDialog';
import KmDialog from './KmDialog';

/* Tabela vozila za kvartal: po mesecu km, litri i potrošnja; ukupno, prosek prethodne godine i granica +5%.
   Km: tahograf (DDD) ili — za analogne tahografe — procena (stanje km / prijavljeno / gorivo) koju operater potvrđuje klikom.
   Klik na litre otvara unos/izmenu goriva izdatog vozilu u tom mesecu (stvarni podaci sa dokumentom). */
export default function TabelaVozila({ data, companyId, onIzmena }) {
    const [izmena, setIzmena] = useState(null);
    const [kmIzmena, setKmIzmena] = useState(null);
    const meseci = data.period.meseci;
    const [pretraga, setPretraga] = useState('');
    const redovi = useMemo(() => filtriraj(data.redovi, pretraga, (r) => [r.reg,
        ...meseci.flatMap((m) => [km(r.meseci[m].km), lit(r.meseci[m].litara), r.meseci[m].dokument, r.meseci[m].izvorOpis]),
        km(r.ukupno.km), lit(r.ukupno.litara)]), [data.redovi, meseci, pretraga]);

    const kmCelija = (red, m) => {
        const c = red.meseci[m];
        const razlika = c.izvor === 'tahograf' && c.kmPrijavljeno != null && c.kmTahograf > 0 && Math.abs(c.kmPrijavljeno - c.kmTahograf) / c.kmTahograf > 0.05;
        const klasa = c.km == null ? undefined : !c.potvrdjen ? 'akc-procena' : razlika ? 'akc-razlika' : undefined;
        const opis = c.km == null ? 'Nema kilometara — kliknite za unos'
            : `${c.izvorOpis}${c.napomenaKm ? ` (${c.napomenaKm})` : ''}${c.kmPrijavljeno != null && c.izvor !== 'prijavljeno' ? ` · prijavljeno ${km(c.kmPrijavljeno)} km` : ''}`
              + `${c.potvrdjen ? '' : ' — kliknite da potvrdite ili ispravite'}`;
        return (
            <button type="button" className={`akc-lit${klasa ? ` ${klasa}` : ''}`} onClick={() => setKmIzmena({ red, mesec: m, celija: c })}
                aria-label={`Kilometri ${red.reg} ${mesecNaziv(m)}`} data-pr-tooltip={opis}>
                {c.km == null ? <span className="muted">+ km</span> : <>{c.potvrdjen ? '' : '≈'}{km(c.km)}{c.izvor === 'rucno' ? <i className="pi pi-check akc-potvrda" aria-hidden="true" /> : null}</>}
            </button>
        );
    };
    const litCelija = (red, m) => {
        const c = red.meseci[m];
        return (
            <button type="button" className="akc-lit" onClick={() => setIzmena({ red, mesec: m, celija: c })}
                aria-label={`Gorivo ${red.reg} ${mesecNaziv(m)}`} data-pr-tooltip={c.dokument ? `Dokument: ${c.dokument}` : 'Unesite gorivo izdato vozilu'}>
                {c.litara == null ? <span className="muted">+ unos</span> : lit(c.litara)}
            </button>
        );
    };

    const zaglavlje = (
        <ColumnGroup>
            <Row>
                <Column header="Vozilo" rowSpan={2} frozen />
                {meseci.map((m) => <Column key={m} header={mesecNaziv(m)} colSpan={3} headerClassName="akc-mesec" />)}
                <Column header="Kvartal" colSpan={3} headerClassName="akc-mesec" />
                <Column header="Prosek pret. god." rowSpan={2} headerTooltip="L/100 km u prethodnoj godini" />
                <Column header="Granica +5%" rowSpan={2} headerTooltip="Najviše litara za refakciju bez obrazloženja" />
            </Row>
            <Row>
                {[...meseci, 'uk'].flatMap((m) => [
                    <Column key={`${m}k`} header="km" />,
                    <Column key={`${m}l`} header="L" />,
                    <Column key={`${m}p`} header="L/100" />,
                ])}
            </Row>
        </ColumnGroup>
    );

    return (
        <>
            <Tooltip target=".akc-tabela [data-pr-tooltip]" position="top" />
            <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (registracija, km, litri, dokument...)" label="Pretraga vozila"
                ukupno={data.redovi.length} pronadjeno={redovi.length} jedinica="vozila" />
            <DataTable value={redovi} headerColumnGroup={zaglavlje} dataKey="vehicleId" size="small" stripedRows scrollable
                className="akc-tabela" emptyMessage={tableEmpty(pretraga.trim() ? 'Nijedno vozilo ne odgovara pretrazi.' : 'Nema vozila sa kilometražom ili gorivom u ovom kvartalu.')}>
                <Column field="reg" frozen className="akc-reg" />
                {meseci.flatMap((m) => [
                    <Column key={`${m}k`} body={(r) => kmCelija(r, m)} className="num" />,
                    <Column key={`${m}l`} body={(r) => litCelija(r, m)} className="num" />,
                    <Column key={`${m}p`} body={(r) => pot(r.meseci[m].potrosnja)} className="num muted-cell" />,
                ])}
                <Column body={(r) => (r.ukupno.procena && r.ukupno.km != null ? <span className="akc-procena">≈{km(r.ukupno.km)}</span> : km(r.ukupno.km))} className="num strong" />
                <Column body={(r) => lit(r.ukupno.litara)} className="num strong" />
                <Column body={(r) => pot(r.ukupno.potrosnja)} className="num" />
                <Column body={(r) => pot(r.prosekPrethodne)} className="num" />
                <Column body={(r) => (r.dozvoljeno == null ? '—' : <span className={r.iznad ? 'akc-iznad' : 'akc-ok'}>{lit(r.dozvoljeno)}</span>)} className="num" />
            </DataTable>
            <p className="akc-legenda muted">
                km iz tahografa (stanje kilometar-sata 1. u mesecu i 1. u sledećem) ·
                <span className="akc-procena"> ≈ procena</span> (nema DDD: iz stanja km, km firme ili goriva — potvrdite pre podnošenja) ·
                <i className="pi pi-check akc-potvrda" aria-hidden="true" /> potvrdio operater ·
                <span className="akc-razlika"> crveno</span> prijavljeno odstupa od tahografa &gt; 5% · klik na km: potvrda ili ispravka · klik na litre: unos goriva sa dokumentom
            </p>
            {kmIzmena && (
                <KmDialog companyId={companyId} red={kmIzmena.red} mesec={kmIzmena.mesec} celija={kmIzmena.celija}
                    onHide={() => setKmIzmena(null)} onSacuvano={() => { setKmIzmena(null); onIzmena(); }} />
            )}
            {izmena && (
                <IzdavanjeDialog companyId={companyId} red={izmena.red} mesec={izmena.mesec} celija={izmena.celija}
                    onHide={() => setIzmena(null)} onSacuvano={() => { setIzmena(null); onIzmena(); }} />
            )}
        </>
    );
}
